import React, { useState, useEffect } from 'react';

import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';

import InfoIcon from '@mui/icons-material/Info';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import Tooltip from '@mui/material/Tooltip';
import Checkbox from '@mui/material/Checkbox';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Chip from '@mui/material/Chip';

import { useSelector, useDispatch } from 'react-redux';
import { updateMenuGroup, clearAPIStatus } from 'store/actions/restaurantMenu';
import ColorPickerDialog from 'views/Common/ColorPickerDialog';
import { noWheelInputProps } from 'util/utils'

const GroupEdit = (props) => {
    const { group, menuId } = props;
    const dispatch = useDispatch();
    const [allow_quantity, setAllow_quantity] = useState(false);
    const [force_min, setForce_min] = useState(null);
    const [force_max, setForce_max] = useState(null);
    const [sort, setSort] = useState(99);
    const [required, setRequired] = useState('optional');
    const [hideForWeb, setHideForWeb] = useState(false);
    const [name, setName] = useState('');

    const apiSuccess = useSelector((state) => state.restaurantMenu.apiSuccess);
    const clearApiStatus = () => dispatch(clearAPIStatus());
    const updateTheGroup = (updatedGroup) => dispatch(updateMenuGroup(updatedGroup));

    const [isColorPickerDialogOpen, setIsColorPickerDialogOpen] = useState(false);
    const [colorHex, setColorHex] = useState(null);

    const close = () => props.editItem(false);

    if (apiSuccess) {
        clearApiStatus();
        close();
    }

    useEffect(() => {
        if (group) {
            setName(group.name);
            setAllow_quantity(group.allow_quantity);
            setForce_min(group.force_min);
            setForce_max(group.force_max);
            if (group.hideForWeb !== undefined) setHideForWeb(group.hideForWeb);
            setRequired(group.required === true ? 'mandatory' : 'optional');
            if (group.sort) setSort(group.sort);
            if (group.colorHex) setColorHex(group.colorHex);
        }
    }, [group]);

    const isRequired = () => required === 'mandatory';

    const save = () => {
        const updateGroup = {
            name,
            required: isRequired(),
            allow_quantity,
            force_min: Number(force_min),
            force_max: Number(force_max),
            sort: Number(sort),
            hideForWeb,
            colorHex,
        };
        let options = group.options;
        if (colorHex) {
            options = group.options.map(option => ({ ...option, colorHex }));
        }
        updateTheGroup({ ...group, ...updateGroup, menuId, options });
        close();
    };

    const isSaveButtonDisabled = () => {
        if (force_max < 0 || force_min < 0) return true;
        if (!name || name.trim() === '') return true;
        if (isRequired() && (!force_min || Number(force_min) < 1)) return true;
        return false;
    };

    return (
        <Card
            draggable
            onDragStart={e => { e.preventDefault(); e.stopPropagation(); }}
            elevation={2}
            sx={{ width: 380, maxWidth: '100%', borderRadius: 2 }}
        >
            {/* Header */}
            <Box sx={{
                px: 2.5, pt: 2, pb: 1.5,
                bgcolor: 'grey.50',
                borderBottom: '1px solid',
                borderColor: 'divider',
            }}>
                <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                    Edit Group
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    Min, max, sort, and visibility for this option group
                </Typography>
            </Box>

            <CardContent sx={{
                px: 2.5, py: 2,
                '&:last-child': { pb: 2 },
                overflow: 'visible',
                minWidth: 0,
            }}>
                <Stack spacing={2.5} sx={{ minWidth: 0 }}>

                    {/* Group Name */}
                    <TextField
                        fullWidth
                        value={name}
                        onChange={e => setName(e.target.value)}
                        label="Group Name"
                        variant="outlined"
                        size="small"
                        autoFocus
                        required
                    />

                    {/* Selection Type */}
                    <FormControl component="fieldset">
                        <FormLabel component="legend" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                            Selection Type
                        </FormLabel>
                        <RadioGroup
                            value={required}
                            name="required"
                            onChange={e => {
                                const val = e.target.value;
                                setRequired(val);
                                if (val === 'mandatory' && (!force_min || Number(force_min) < 1)) {
                                    setForce_min(1);
                                }
                            }}
                            row
                        >
                            <FormControlLabel
                                value="optional"
                                control={<Radio size="small" color="primary" />}
                                label={<Typography variant="body2">Optional</Typography>}
                            />
                            <FormControlLabel
                                value="mandatory"
                                control={<Radio size="small" color="primary" />}
                                label={<Typography variant="body2">Mandatory</Typography>}
                            />
                        </RadioGroup>
                    </FormControl>

                    <Divider />

                    {/* Min / Max / Sort — CSS grid + minmax(0,1fr) avoids flex/outline overlap; margin none on fields */}
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Selection limits
                        </Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                                columnGap: 1.5,
                                width: '100%',
                                minWidth: 0,
                            }}
                        >
                            {[
                                { label: 'Min', value: force_min ?? '', onChange: setForce_min },
                                { label: 'Max', value: force_max ?? '', onChange: setForce_max },
                                { label: 'Sort', value: sort, onChange: setSort },
                            ].map((field) => (
                                <TextField
                                    key={field.label}
                                    fullWidth
                                    margin="none"
                                    size="small"
                                    label={field.label}
                                    type="number"
                                    InputLabelProps={{ shrink: true }}
                                    variant="outlined"
                                    value={field.value}
                                    onChange={(e) => field.onChange(e.target.value)}
                                    inputProps={{ min: 0, ...noWheelInputProps }}
                                    sx={{
                                        width: '100%',
                                        maxWidth: '100%',
                                        minWidth: 0,
                                        '& .MuiOutlinedInput-root': {
                                            width: '100%',
                                        },
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>

                    <Divider />

                    {/* Allow Quantity */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minHeight: 40 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2">Allow duplicate selections</Typography>
                            <Tooltip title="Customers can select the same choice more than once." arrow placement="top">
                                <InfoIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help' }} />
                            </Tooltip>
                        </Box>
                        <Checkbox
                            size="small"
                            color="primary"
                            checked={allow_quantity}
                            onChange={() => setAllow_quantity(v => !v)}
                            inputProps={{ 'aria-label': 'Allow duplicate selections' }}
                        />
                    </Box>

                    {/* Hide for Web */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minHeight: 40 }}>
                        <Typography variant="body2">Hide for web ordering</Typography>
                        <Switch
                            size="small"
                            checked={hideForWeb}
                            onChange={e => setHideForWeb(e.target.checked)}
                            name="hideForWeb"
                            color="primary"
                        />
                    </Box>

                    <Divider />

                    {/* Color Picker — same row pattern as toggles */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minHeight: 40 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PaletteOutlinedIcon />}
                            onClick={() => setIsColorPickerDialogOpen(true)}
                            sx={{ flexShrink: 0 }}
                        >
                            Accent color
                        </Button>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', minWidth: 0 }}>
                            {colorHex ? (
                                <Tooltip title={`Color: ${colorHex}`} arrow>
                                    <Chip
                                        size="small"
                                        label={colorHex}
                                        onClick={() => setIsColorPickerDialogOpen(true)}
                                        sx={{
                                            bgcolor: colorHex,
                                            color: 'white',
                                            fontWeight: 600,
                                            fontSize: '0.7rem',
                                            border: '1px solid rgba(0,0,0,0.12)',
                                            textShadow: '0 1px 2px rgba(0,0,0,0.4)',
                                            cursor: 'pointer',
                                            maxWidth: '100%',
                                        }}
                                    />
                                </Tooltip>
                            ) : (
                                <Typography variant="caption" color="text.disabled" sx={{ textAlign: 'right' }}>
                                    Not set
                                </Typography>
                            )}
                        </Box>
                    </Box>

                </Stack>
            </CardContent>

            <ColorPickerDialog
                isOpen={isColorPickerDialogOpen}
                onClose={() => setIsColorPickerDialogOpen(false)}
                onColorSelected={setColorHex}
            />

            <Divider />

            <CardActions sx={{ px: 2.5, py: 1.5, justifyContent: 'flex-end', gap: 1 }}>
                <Button size="small" onClick={close} color="inherit">
                    Cancel
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    disabled={isSaveButtonDisabled()}
                    color="primary"
                    onClick={save}
                    disableElevation
                >
                    Save
                </Button>
            </CardActions>
        </Card>
    );
};

export default GroupEdit;